import React, { useEffect, useMemo } from "react";

import { Form, Formik } from "formik";
import toast from "react-hot-toast";
import * as Yup from "yup";

import FacebookSVG from "../../../assets/svg/facebook.svg";
import InstagramSVG from "../../../assets/svg/instagram.svg";
import LinkedInSVG from "../../../assets/svg/linkedin.svg";
import XSVG from "../../../assets/svg/x.svg";
import YouTubeSVG from "../../../assets/svg/youtube2.svg";

import { User as UserM } from "../../../models/user";
import {
  useGetUserQuery,
  useUpdateUserMutation,
} from "../../../redux/services/user";

import Button from "../../../components/form/Button";
import Input from "../../../components/form/Input";
import TemplateContainer from "../../TemplateForm/components/common/TemplateContainer";
import ProfilePhoto from "./ProfilePhoto";

const urlRegex = /^(https?:\/\/)?([\w.-]+)\.[a-z]{2,}.*$/i;

// Platform-specific domain patterns
const PLATFORM_DOMAINS = {
  instagram: /^(https?:\/\/)?(www\.)?instagram\.com/i,
  youtube: /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)/i,
  facebook: /^(https?:\/\/)?(www\.)?(facebook\.com|fb\.com)/i,
  x: /^(https?:\/\/)?(www\.)?(x\.com|twitter\.com)/i,
  linkedin: /^(https?:\/\/)?(www\.)?linkedin\.com/i,
} as const;

const createPlatformValidator = (platform: keyof typeof PLATFORM_DOMAINS) => {
  return Yup.string()
    .nullable()
    .test("is-valid-url", "Invalid URL format", function (value) {
      if (!value) return true;
      return urlRegex.test(value);
    })
    .test(
      "is-platform-url",
      `Must be a valid ${platform.charAt(0).toUpperCase() + platform.slice(1)} URL`,
      function (value) {
        if (!value) return true;
        return PLATFORM_DOMAINS[platform].test(value);
      }
    );
};

const socialLinksSchema = Yup.object().shape({
  instagram: createPlatformValidator("instagram"),
  youtube: createPlatformValidator("youtube"),
  facebook: createPlatformValidator("facebook"),
  x: createPlatformValidator("x"),
  linkedin: createPlatformValidator("linkedin"),
});

const schema = Yup.object().shape({
  firstName: Yup.string().max(100, "Too long").required("Required"),
  lastName: Yup.string().max(100, "Too long").required("Required"),
  bio: Yup.string().max(300, "Maximum 300 characters").nullable(),
  socialLinks: socialLinksSchema,
});

const SOCIAL_LINKS_CONFIG = {
  instagram: {
    label: "Instagram",
    placeholder: "https://instagram.com",
    icon: InstagramSVG,
  },
  youtube: {
    label: "YouTube",
    placeholder: "https://youtube.com",
    icon: YouTubeSVG,
  },
  facebook: {
    label: "Facebook",
    placeholder: "https://facebook.com",
    icon: FacebookSVG,
  },
  x: {
    label: "X",
    placeholder: "https://x.com",
    icon: XSVG,
  },
  linkedin: {
    label: "LinkedIn",
    placeholder: "https://linkedin.com",
    icon: LinkedInSVG,
  },
} as const;

type SocialPlatform = keyof typeof SOCIAL_LINKS_CONFIG;

const ProfileDetails = () => {
  const { data: user } = useGetUserQuery();
  const initialValues = useMemo(
    () => ({
      firstName: user?.firstName ?? "",
      lastName: user?.lastName ?? "",
      bio: user?.bio ?? "",
      socialLinks: user?.socialLinks ?? {},
    }),
    [user]
  );

  const [
    updateUser,
    {
      isLoading: isLoadingUpdate,
      isSuccess: isSuccessUpdate,
      isError: isErrorUpdate,
      error: errorUpdate,
      data: updateResponse,
    },
  ] = useUpdateUserMutation();

  useEffect(() => {
    if (isSuccessUpdate && updateResponse?.userId) {
      toast.success("User profile updated.");
    }
  }, [isSuccessUpdate, updateResponse]);

  useEffect(() => {
    if (isErrorUpdate) {
      toast.error(errorUpdate?.data?.detail ?? "Failed to update.");
    }
  }, [isErrorUpdate, errorUpdate]);

  const handleSave = async (values: Partial<UserM>) => {
    await updateUser(values)
      .unwrap()
      .catch(() => {});
  };

  const getSocialLinkError = (
    platform: SocialPlatform,
    errors: any
  ): string | undefined => {
    const socialErrors = errors?.socialLinks;
    if (
      !socialErrors ||
      typeof socialErrors === "string" ||
      Array.isArray(socialErrors)
    ) {
      return undefined;
    }
    const error = socialErrors[platform];
    return typeof error === "string" ? error : undefined;
  };

  return (
    <TemplateContainer>
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={schema}
        onSubmit={(values) => handleSave(values)}
      >
        {(props) => {
          const { values, errors, setFieldValue, handleSubmit } = props;

          const enableSave =
            initialValues.firstName !== values.firstName ||
            initialValues.lastName !== values.lastName ||
            initialValues.bio !== values.bio ||
            JSON.stringify(initialValues.socialLinks) !==
              JSON.stringify(values.socialLinks);

          return (
            <Form
              className="space-y-8"
              onSubmit={handleSubmit}
            >
              {/* Personal Details Section */}
              <section className="space-y-6">
                <header>
                  <h2 className="text-lg font-semibold text-gray-900 mb-0">
                    Personal Details
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Update your name, bio, and profile picture.
                  </p>
                </header>

                <ProfilePhoto />

                {/* Name Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    id="firstName"
                    label="First Name"
                    name="firstName"
                    type="text"
                    maxLength={100}
                    value={values.firstName}
                    errorMessage={errors?.firstName as string}
                    errorPosition="bottom-left"
                    onChange={(e) => setFieldValue("firstName", e.target.value)}
                    required
                  />
                  <Input
                    id="lastName"
                    label="Last Name"
                    name="lastName"
                    type="text"
                    maxLength={100}
                    value={values.lastName}
                    errorMessage={errors?.lastName as string}
                    errorPosition="bottom-left"
                    onChange={(e) => setFieldValue("lastName", e.target.value)}
                    required
                  />
                </div>

                {/* Bio Field */}
                <Input
                  id="bio"
                  label="Bio"
                  name="bio"
                  type="text"
                  maxLength={300}
                  value={values.bio}
                  errorMessage={errors?.bio as string}
                  errorPosition="bottom-left"
                  tooltip='This is your default bio that will be added to the bottom of your articles in the "Article by" section. This can be overwritten in each template configuration.'
                  onChange={(e) => setFieldValue("bio", e.target.value)}
                />

                {/* Email Display */}
                <div className="space-y-2">
                  <label className="block text-xs font-medium uppercase tracking-wide text-gray-900">
                    Email Address
                  </label>
                  <div className="text-sm text-gray-600 py-2 px-3 bg-gray-50 rounded-md border">
                    {user?.email}
                  </div>
                </div>
              </section>

              {/* Divider */}
              <hr className="border-gray-200" />

              {/* Social Links Section */}
              <section className="space-y-6">
                <header>
                  <h2 className="text-lg font-semibold text-gray-900 mb-0">
                    Social Links
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Links added here will appear at the bottom of your published
                    articles when enabled.
                  </p>
                </header>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {(Object.keys(SOCIAL_LINKS_CONFIG) as SocialPlatform[]).map(
                    (platform) => {
                      const { label, placeholder, icon } =
                        SOCIAL_LINKS_CONFIG[platform];

                      return (
                        <Input
                          key={platform}
                          name={`socialLinks.${platform}`}
                          label={
                            <div className="flex items-center gap-2">
                              <img
                                src={icon}
                                alt={`${label} icon`}
                                className="w-5 h-5 object-contain"
                              />
                              <span>{label}</span>
                            </div>
                          }
                          placeholder={placeholder}
                          value={values?.socialLinks?.[platform] ?? ""}
                          errorMessage={getSocialLinkError(platform, errors)}
                          errorPosition="bottom-left"
                          onChange={(e) =>
                            setFieldValue("socialLinks", {
                              ...values?.socialLinks,
                              [platform]: e.target.value,
                            })
                          }
                        />
                      );
                    }
                  )}
                </div>
              </section>

              {/* Action Bar */}
              <div className="flex pt-6 border-t border-gray-200">
                <Button
                  type="submit"
                  variant={enableSave ? "primary" : "disabled"}
                  loading={isLoadingUpdate}
                  onClick={() => {}}
                  className="min-w-32"
                >
                  {isLoadingUpdate ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </TemplateContainer>
  );
};

export default ProfileDetails;
